import { sagaIntl } from '@i18n';
import * as Actions from '../actions';
import {
  selectCurrentTdoId,
  selectGlobalSettings,
  selectHistory,
} from '../selectors';
import { settingsAsUserChange, updateSettingsOnDetails } from '../services';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { isWorkerAction, markWorkerAction } from '@utils';
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import {
  BULK_ADD_BACKGROUND_BLUR,
  CHANGE_CODE,
  CHANGE_SHAPE,
  IN_LOAD_TRACKING_ENGINE_RESULTS,
  SET_DETECTION_TYPE_REDACTION,
  SET_INDIVIDUAL_REDACTION,
} from '@worker';
import { HistoryMetadata } from '../store.models';
import {
  ACTION_TYPE,
  ActionType,
  REDACTION_CODE_CHANGE,
} from '@redact/components/MediaDetailsPage/Editor/types';

/**
 * VE-30516: undo/redo of a global-settings change has to reach the server.
 *
 * Settings entries go on the history stack as bare immer patches with no
 * `inverseAction`, so `onUndoAction` reverts them in redux and the watchers
 * below dispatch nothing. That used to be covered by accident: every SAVE
 * resent redux's whole `details`, settings included, so the undone value was
 * persisted on the way past. The details writers now send only the keys they
 * own, so the accident is gone — and because `TDO_NAME_CHANGE_SUCCESS` and
 * `FETCH_UPDATE_TDO_DETAILS_SUCCESS` replace `tdo.details` wholesale from the
 * server, a later rename or image burn-in would overwrite the undone value and
 * the undo would visibly reverse itself.
 *
 * An undo IS a user-made settings change — the user chose these values — so it
 * persists exactly like one, stamp included. `updateSettingsOnDetails` is called
 * directly rather than dispatching `SET_GLOBAL_SETTINGS`, which would push a
 * fresh entry onto the very stack being unwound.
 */
const touchesGlobalSettings = (patches: unknown): boolean =>
  Array.isArray(patches) &&
  patches.some((patch) => {
    const path = (patch as { path?: unknown[] })?.path;
    return (
      Array.isArray(path) &&
      path[0] === 'tdo' &&
      path[1] === 'details' &&
      path[2] === 'settings'
    );
  });

function* persistGlobalSettingsAfterHistoryMove() {
  const tdoId = yield* select(selectCurrentTdoId);
  if (!tdoId) {
    return;
  }
  const settings = yield* select(selectGlobalSettings);
  yield* put(updateSettingsOnDetails(tdoId, settingsAsUserChange(settings)));
}

export function* undoSaga() {
  yield* all([
    fork(watchUndoAction),
    fork(watchRedoAction),
    fork(watchPatchEmptyUndoAction),
    fork(watchJumpToUndoPoint),
    fork(watchJumpToRedoPoint),
  ]);
}

export function* watchUndoAction() {
  yield* takeEvery(Actions.UNDO_ACTION, function* (action) {
    if (isWorkerAction(action)) {
      return;
    }

    const { past } = yield* select(selectHistory);
    const undoAction = [...past].pop();
    if (undoAction !== undefined) {
      yield* put(Actions.PATCH_UNDO_ACTION());
      if (
        undoAction !== null &&
        'inversePatches' in undoAction &&
        touchesGlobalSettings(undoAction.inversePatches)
      ) {
        yield* call(persistGlobalSettingsAfterHistoryMove);
      }
      if (undoAction !== null && 'inverseAction' in undoAction) {
        const {
          inverseAction: { creator, payload },
        } = undoAction;
        yield* put(markWorkerAction(creator(payload as any)));
        yield* put(
          Actions.actionLogAuditEvent(
            `Undo Action: { creator: ${creator.name}, payload: ${JSON.stringify(payload)} }`
          )
        );
      } else {
        yield* put(Actions.workerUndoAction());

        if (undoAction !== null) {
          // if we remove null as type can remove this check
          yield* put(
            Actions.actionLogAuditEvent(`Undo: ${JSON.stringify(undoAction)}`) // TODO do we really want to log this whole data structure?
          );
        }
      }
    } else {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          variant: 'error',
          message: intl.formatMessage({
            id: 'cannotUndo',
            defaultMessage: 'Cannot undo last action',
          }),
        })
      );
    }
  });
}

export function* watchRedoAction() {
  yield* takeEvery(Actions.REDO_ACTION, function* (action) {
    if (isWorkerAction(action)) {
      return;
    }

    const { future } = yield* select(selectHistory);
    const redoAction = [...future].pop();
    if (redoAction !== undefined) {
      yield* put(Actions.PATCH_REDO_ACTION());
      if (
        redoAction !== null &&
        'patches' in redoAction &&
        touchesGlobalSettings(redoAction.patches)
      ) {
        yield* call(persistGlobalSettingsAfterHistoryMove);
      }
      if (redoAction !== null && 'action' in redoAction) {
        const {
          action: { creator, payload },
        } = redoAction;
        yield* put(markWorkerAction(creator(payload as any)));
        yield* put(
          Actions.actionLogAuditEvent(
            `Redo Action: { creator: ${creator.name}, payload: ${JSON.stringify(payload)} }`
          )
        );
      } else {
        yield* put(Actions.workerRedoAction());
        if (redoAction !== null) {
          // if we remove null as type can remove this check
          yield* put(
            Actions.actionLogAuditEvent(`Redo: ${JSON.stringify(redoAction)}`) // TODO do we really want to log this whole data structure?
          );
        }
      }
    } else {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          variant: 'error',
          message: intl.formatMessage({
            id: 'cannotRedo',
            defaultMessage: 'Cannot redo previous action',
          }),
        })
      );
    }
  });
}

function getHistoryMetadataByAction(action: any): HistoryMetadata {
  const intl = sagaIntl();

  // TODO use type of action so we don't have to use a bunch type casting inside this function

  switch (action.type) {
    case CHANGE_CODE.type: {
      return {
        description:
          action.payload.changeRedactionCodeType === REDACTION_CODE_CHANGE.ADD
            ? intl.formatMessage({
                id: 'addRedactionCodeDesc',
                defaultMessage: 'add redaction code',
              })
            : action.payload.changeRedactionCodeType ===
                REDACTION_CODE_CHANGE.REMOVE
              ? intl.formatMessage({
                  id: 'removeRedactionCodeDesc',
                  defaultMessage: 'remove redaction code',
                })
              : intl.formatMessage({
                  id: 'updateRedactionCodeDesc',
                  defaultMessage: 'update redaction code',
                }),
      };
    }
    case CHANGE_SHAPE.type: {
      const payload = action.payload as Actions.ChangeShapeRequest;
      const typeObject = payload.typeObject as string;
      const udrGroupIds = payload.udrGroupIds;
      const detectionGroups = payload.detectionGroups;

      const isMergedGroup =
        (udrGroupIds?.length ?? 0) + (detectionGroups?.length ?? 0) > 1;

      if (isMergedGroup) {
        return {
          description: intl.formatMessage({
            id: 'changeMergedGroupShape',
            defaultMessage: 'change merged group shape',
          }),
        };
      }

      return {
        description:
          typeObject === 'udr'
            ? intl.formatMessage({
                id: 'changeUDRShape',
                defaultMessage: 'change UDR shape',
              })
            : intl.formatMessage(
                {
                  id: 'changeDetectionShape',
                  defaultMessage: 'change {typeObject} shape',
                },
                { typeObject }
              ),
      };
    }
    case Actions.ON_SPRAY_PAINT_SAVE.type:
      {
        const actionType = action.payload.actionType as ActionType;
        const isResizeByKeyboard = action.payload.isResizeByKeyboard as boolean;
        if (actionType === ACTION_TYPE.LIVE_TRACKING) {
          return {
            description: intl.formatMessage({
              id: 'liveTrackUdr',
              defaultMessage: 'live track udr',
            }),
          };
        }
        if (actionType === ACTION_TYPE.RESIZE) {
          return {
            description: intl.formatMessage({
              id: 'resizeUDR',
              defaultMessage: 'resize UDR',
            }),
          };
        }
        if (actionType === ACTION_TYPE.MOVE) {
          return {
            description: intl.formatMessage({
              id: 'moveUDR',
              defaultMessage: 'move UDR',
            }),
          };
        }
        if (isResizeByKeyboard) {
          return {
            description: intl.formatMessage({
              id: 'resizeUDR',
              defaultMessage: 'resize UDR',
            }),
          };
        }
      }
      return {
        description: intl.formatMessage({
          id: 'moveOrResizeUDR',
          defaultMessage: 'move/resize UDR',
        }),
      };
    case Actions.NEW_OVERLAY.type:
      return {
        description: intl.formatMessage({
          id: 'drawUDR',
          defaultMessage: 'draw UDR',
        }),
      };
    case Actions.CHANGE_OVERLAY.type: {
      const actionType = action.payload.actionType as ActionType;
      const type = action.payload.type as string;
      if (actionType === ACTION_TYPE.MOVE) {
        return {
          description: intl.formatMessage(
            {
              id: 'moveDetectionOverlay',
              defaultMessage: 'move {type} overlay',
            },
            { type }
          ),
        };
      } else {
        return {
          description: intl.formatMessage(
            {
              id: 'resizeDetectionOverlay',
              defaultMessage: 'resize {type} overlay',
            },
            { type }
          ),
        };
      }
    }
    case Actions.RESIZE_OVERLAY_SEGMENT.type:
      return {
        description: intl.formatMessage({
          id: 'resizeOverlaySegment',
          defaultMessage: 'resize overlay segment',
        }),
      };
    case SET_INDIVIDUAL_REDACTION.type: {
      const changeType = action.payload
        .changeType as Actions.REDACTION_CONFIG_CHANGE_TYPE;
      if (changeType === Actions.REDACTION_CONFIG_CHANGE_TYPE.ADD) {
        return {
          description: intl.formatMessage({
            id: 'addCustomFillEffect',
            defaultMessage: 'add custom fill effect',
          }),
        };
      }
      if (changeType === Actions.REDACTION_CONFIG_CHANGE_TYPE.REMOVE) {
        return {
          description: intl.formatMessage({
            id: 'removeCustomFillEffect',
            defaultMessage: 'remove custom fill effect',
          }),
        };
      }
      if (changeType === Actions.REDACTION_CONFIG_CHANGE_TYPE.EDIT) {
        return {
          description: intl.formatMessage({
            id: 'updateCustomFillEffect',
            defaultMessage: 'update custom fill effect',
          }),
        };
      }
      return {
        description: intl.formatMessage({
          id: 'editCustomFillEffect',
          defaultMessage: 'edit custom fill effect',
        }),
      };
    }
    case SET_DETECTION_TYPE_REDACTION.type: {
      const type = action.payload.detectionType as string;
      if (action.payload.redactionConfig) {
        return {
          description: intl.formatMessage(
            {
              id: 'bulkAddCustomFill',
              defaultMessage: 'Bulk add {type} custom fill',
            },
            { type }
          ),
        };
      } else {
        return {
          description: intl.formatMessage(
            {
              id: 'bulkRemoveCustomFill',
              defaultMessage: 'Bulk remove {type} custom fill',
            },
            { type }
          ),
        };
      }
    }
    case BULK_ADD_BACKGROUND_BLUR.type:
      return {
        description: intl.formatMessage({
          id: 'bulkAddBackgroundBlur',
          defaultMessage: 'bulk add background blur',
        }),
      };
    case Actions.CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM_SUBMIT.type: {
      const changeType = action.payload?.changeType as string;
      const location = action.payload?.location as string;

      if (changeType === 'stick') {
        return {
          description: intl.formatMessage({
            id: 'stickUdrToVideo',
            defaultMessage: 'stick UDR to video',
          }),
        };
      } else if (location === 'udr') {
        return {
          description: intl.formatMessage({
            id: 'setUdrTimeSpan',
            defaultMessage: 'set UDR time span',
          }),
        };
      } else if (location === 'timeline') {
        return {
          description: intl.formatMessage({
            id: 'adjustUdrTimeSpanOnTimeline',
            defaultMessage: 'adjust UDR time span on timeline',
          }),
        };
      }

      return {};
    }
    case IN_LOAD_TRACKING_ENGINE_RESULTS.type:
      return {
        // TODO: handle load tracking engine results
        description: 'load tracking results',
      };
    default:
      return {};
  }
}
export function* watchPatchEmptyUndoAction() {
  yield* takeLatest(
    [
      CHANGE_CODE,
      CHANGE_SHAPE,
      Actions.NEW_OVERLAY,
      Actions.CHANGE_OVERLAY,
      SET_INDIVIDUAL_REDACTION,
      Actions.ON_SPRAY_PAINT_SAVE,
      Actions.RESIZE_OVERLAY_SEGMENT,
      SET_DETECTION_TYPE_REDACTION,
      BULK_ADD_BACKGROUND_BLUR,
      Actions.CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM_SUBMIT,
      IN_LOAD_TRACKING_ENGINE_RESULTS,
    ],
    function* (action) {
      let historyMetadata: HistoryMetadata = {};
      historyMetadata = getHistoryMetadataByAction(action);

      yield* put(
        Actions.PATCH_EMPTY_UNDO_ACTION({
          clearFuture: action.type !== IN_LOAD_TRACKING_ENGINE_RESULTS.type,
          historyMetadata,
        })
      );
    }
  );
}

export function* watchJumpToUndoPoint() {
  yield* takeEvery(Actions.JUMP_TO_UNDO_POINT, function* (action) {
    const { steps } = action.payload;
    const intl = sagaIntl();

    if (steps <= 0) {
      return;
    }

    for (let i = 0; i < steps; i++) {
      const { past } = yield* select(selectHistory);

      if (past.length === 0) {
        yield* put(
          enqueueSnackbar({
            variant: 'warning',
            message: intl.formatMessage({
              id: 'cannotUndoFurther',
              defaultMessage: 'Cannot undo further',
            }),
          })
        );
        break;
      }

      const undoAction = [...past].pop();
      if (undoAction !== undefined) {
        yield* put(Actions.PATCH_UNDO_ACTION());
        if (undoAction !== null && 'inverseAction' in undoAction) {
          const {
            inverseAction: { creator, payload },
          } = undoAction;
          yield* put(markWorkerAction(creator(payload as any)));
        } else {
          yield* put(Actions.workerUndoAction());
        }
      }
    }

    yield* put(
      enqueueSnackbar({
        variant: 'success',
        message: intl.formatMessage(
          {
            id: 'jumpedBackSteps',
            defaultMessage:
              'Jumped back {steps, plural, one {# step} other {# steps}}',
          },
          { steps }
        ),
      })
    );
  });
}

export function* watchJumpToRedoPoint() {
  yield* takeEvery(Actions.JUMP_TO_REDO_POINT, function* (action) {
    const { steps } = action.payload;
    const intl = sagaIntl();

    if (steps <= 0) {
      return;
    }

    for (let i = 0; i < steps; i++) {
      const { future } = yield* select(selectHistory);

      if (future.length === 0) {
        yield* put(
          enqueueSnackbar({
            variant: 'warning',
            message: intl.formatMessage({
              id: 'cannotRedoFurther',
              defaultMessage: 'Cannot redo further',
            }),
          })
        );
        break;
      }

      const redoAction = [...future].pop();
      if (redoAction !== undefined) {
        yield* put(Actions.PATCH_REDO_ACTION());
        if (redoAction !== null && 'action' in redoAction) {
          const {
            action: { creator, payload },
          } = redoAction;
          yield* put(markWorkerAction(creator(payload as any)));
        } else {
          yield* put(Actions.workerRedoAction());
        }
      }
    }

    yield* put(
      enqueueSnackbar({
        variant: 'success',
        message: intl.formatMessage(
          {
            id: 'jumpedForwardSteps',
            defaultMessage:
              'Jumped forward {steps, plural, one {# step} other {# steps}}',
          },
          { steps }
        ),
      })
    );
  });
}
